import { Reorder } from "framer-motion";
import { ChevronUp, ChevronDown, GripVertical } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { RULE_HI } from "@/lib/aiot";

export const Mcq = ({ item, value, onChange }) => {
  const { L } = useLang();
  return (
    <div className="grid gap-2.5" data-testid="test-mcq">
      {item.options.map((o, i) => (
        <button key={o} type="button" onClick={() => onChange(i)} className={`text-left rounded-xl border-2 px-4 py-3.5 text-[15px] font-semibold transition-colors ${value === i ? "border-[#0F766E] bg-[#F0FDFA] text-[#0B1F3A]" : "border-slate-200 bg-white hover:border-[#5EEAD4]"}`} data-testid={`test-mcq-option-${i}`}>
          <span className={`inline-grid place-items-center w-7 h-7 mr-3 rounded-full text-xs font-black ${value === i ? "bg-[#0F766E] text-white" : "bg-slate-100 text-slate-500"}`}>{String.fromCharCode(65 + i)}</span>{L(o, item.options_hi?.[i])}
        </button>
      ))}
    </div>
  );
};

const COLORS = ["#DC2626", "#F59E0B", "#1D4ED8", "#059669", "#7C3AED"];

export const Wire = ({ item, value, onChange }) => {
  const { L } = useLang();
  const v = value || {};
  const pins = item.pins, board = item.board_pins;
  const H = Math.max(board.length, pins.length) * 34 + 30;
  const py = (i, n) => 30 + i * ((H - 50) / Math.max(1, n - 1));
  return (
    <div data-testid="test-wire-task">
      <svg viewBox={`0 0 460 ${H}`} className="w-full rounded-2xl border border-slate-200 bg-[#0B1F3A]">
        <rect x="20" y="10" width="120" height={H - 20} rx="12" fill="#134E4A" />
        <text x="80" y={H / 2} fill="#99F6E4" fontSize="11" fontWeight="700" textAnchor="middle" transform={`rotate(-90 80 ${H / 2})`} data-testid="test-wire-module">{L(item.module, item.module_hi)}</text>
        <rect x="320" y="10" width="120" height={H - 20} rx="12" fill="#1E3A8A" />
        <text x="400" y={H / 2} fill="#BFDBFE" fontSize="12" fontWeight="800" textAnchor="middle" transform={`rotate(90 400 ${H / 2})`}>ESP32</text>
        {pins.map((p, i) => <g key={p}><circle cx="140" cy={py(i, pins.length)} r="6" fill={COLORS[i % 5]} /><text x="132" y={py(i, pins.length) + 4} fill="#fff" fontSize="11" textAnchor="end" fontWeight="700">{p}</text></g>)}
        {board.map((b, j) => <g key={b}><circle cx="320" cy={py(j, board.length)} r="5" fill="#CBD5E1" /><text x="328" y={py(j, board.length) + 4} fill="#E2E8F0" fontSize="10" fontWeight="600">{b}</text></g>)}
        {pins.map((p, i) => {
          const j = board.indexOf(v[p]);
          if (j < 0) return null;
          const y1 = py(i, pins.length), y2 = py(j, board.length);
          return <path key={p} d={`M140 ${y1} C 230 ${y1}, 230 ${y2}, 320 ${y2}`} stroke={COLORS[i % 5]} strokeWidth="3.5" fill="none" strokeLinecap="round" />;
        })}
      </svg>
      <div className="mt-3 grid sm:grid-cols-3 gap-2">
        {pins.map((p, i) => (
          <label key={p} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
            <span className="font-bold" style={{ color: COLORS[i % 5] }}>{p}</span> → 
            <select value={v[p] || ""} onChange={(e) => onChange({ ...v, [p]: e.target.value })} className="ml-1 rounded-lg border border-slate-300 px-2 py-1 text-sm" data-testid={`test-wire-${p}`}>
              <option value="">{L("choose pin", "पिन चुनें")}</option>
              {board.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </label>
        ))}
      </div>
    </div>
  );
};

export const Rule = ({ item, value, onChange }) => {
  const { L, lang } = useLang();
  const v = value || {};
  const parts = [["trigger", "triggers", L("WHEN", "जब"), "#1D4ED8"], ["condition", "conditions", L("AND", "और"), "#B45309"], ["action", "actions", L("THEN", "तब"), "#059669"]];
  return (
    <div className="grid md:grid-cols-3 gap-3" data-testid="test-rule-task">
      {parts.map(([k, list, tag, c]) => (
        <div key={k} className="rounded-2xl border-2 bg-white p-3" style={{ borderColor: c }}>
          <div className="text-xs font-black tracking-widest" style={{ color: c }}>{tag}</div>
          <select value={v[k] || ""} onChange={(e) => onChange({ ...v, [k]: e.target.value })} className="mt-2 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm" data-testid={`test-rule-${k}`}>
            <option value="">{L("choose…", "चुनें…")}</option>
            {item.options[list].map(([key, label]) => <option key={key} value={key}>{lang === "hi" ? RULE_HI[key] || label : label}</option>)}
          </select>
        </div>
      ))}
    </div>
  );
};

export const Order = ({ item, value, onChange }) => {
  const { L } = useLang();
  const list = value || item.items;
  const move = (i, d) => { const n = [...list]; [n[i], n[i + d]] = [n[i + d], n[i]]; onChange(n); };
  return (
    <Reorder.Group axis="y" values={list} onReorder={onChange} className="space-y-2" data-testid="test-order-task">
      {list.map((x, i) => (
        <Reorder.Item key={x} value={x} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 cursor-grab active:cursor-grabbing" data-testid={`test-order-item-${i}`}>
          <GripVertical className="w-4 h-4 text-slate-400" />
          <span className="w-6 h-6 rounded-full bg-[#0B1F3A] text-white text-xs font-bold grid place-items-center">{i + 1}</span>
          <span className="flex-1 text-sm font-semibold text-[#0B1F3A]" data-testid={`test-order-text-${i}`}>{L(x, item.items_hi?.[x])}</span>
          <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30" aria-label="Move up" data-testid={`test-order-up-${i}`}><ChevronUp className="w-4 h-4" /></button>
          <button type="button" disabled={i === list.length - 1} onClick={() => move(i, 1)} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30" aria-label="Move down" data-testid={`test-order-down-${i}`}><ChevronDown className="w-4 h-4" /></button>
        </Reorder.Item>
      ))}
    </Reorder.Group>
  );
};
